import { getUnitCount } from '../../helpers/getUnitCount';

import { HorizontalContainer, Wrapper } from '@american-autonomy/ag-ui';

import { getPartLabels } from 'pages/MaintenancePage/components/ServiceIntervalConfigPanel/helpers/getPartLabels';
import { DUE_SOON_PERCENT } from 'pages/MaintenancePage/constants';
import { getServiceHoursRemaining } from 'pages/MaintenancePage/helpers/getServiceHoursRemaining';
import { getUnitFormatters } from 'pages/MaintenancePage/helpers/getUnitFormatters';
import { getUnitLabels } from 'pages/MaintenancePage/helpers/getUnitLabels';
import { getUsageUnit } from 'pages/MaintenancePage/helpers/getUsageUnit';
import { getWearPercent } from 'pages/MaintenancePage/helpers/getWearPercent';
import useTranslation from 'pages/MaintenancePage/hooks/useTranslation';

import { TEST_ID } from './constants';
import { getTargetUsage } from './helpers/getTargetUsage';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

// Inline wear status for the drafted service target — reads the effective
// (record-adjusted) counters the panel receives, so the line matches the rings.
const TargetStatusLine: FunctionComponent<Props> = ({
    className = '',
    draft,
    drone,
    serviceIntervals,
    testId = TEST_ID
}) => {
    const t = useTranslation();
    const { eventType, partType, unitSelection } = draft;
    // Replaced/Serviced always target a part; an inspection targets one only when
    // the operator picks a part — its time-since-service context is just as
    // meaningful there. A general (no-part) inspection has no target to report.
    const isPartSelectable =
        eventType === 'replaced' || eventType === 'serviced' || eventType === 'inspected';

    if (!isPartSelectable || !partType) {
        return null;
    }

    // A multi-unit part has no target until a unit (or 'All units') is chosen.
    if (getUnitCount({ drone, partType }) > 1 && unitSelection.kind === 'unchosen') {
        return null;
    }

    const interval = serviceIntervals[partType];
    const usage = getTargetUsage({ drone, partType, unitSelection });
    const serviceCountdown = getServiceHoursRemaining({ interval, usage });
    const wear = getWearPercent({ interval, usage });

    const unitLabels = getUnitLabels(t);

    const formatUnit = getUnitFormatters(t)[partType];
    const targetLabel =
        unitSelection.kind === 'unit' && formatUnit
            ? formatUnit({ number: unitSelection.unitNumber })
            : getPartLabels(t)[partType];

    const usageUnit = getUsageUnit({ interval, partType });
    const usageText = `${usage} ${unitLabels[usageUnit]} ${t.SINCE_SERVICE_SUFFIX}`;

    const statusText = !serviceCountdown.configured
        ? t.NOT_CONFIGURED
        : serviceCountdown.overdue
          ? t.SERVICE_OVERDUE
          : `${serviceCountdown.remaining} ${unitLabels[serviceCountdown.unit]} ${t.TO_SERVICE_SUFFIX}`;

    const isOverdue = serviceCountdown.configured && serviceCountdown.overdue;
    const isDueSoon = wear.configured && wear.percentage >= DUE_SOON_PERCENT;
    const statusClass = isOverdue ? styles.isOverdue : isDueSoon ? styles.isDueSoon : '';

    return (
        <HorizontalContainer className={`${styles.targetStatusLine} ${className}`} testId={testId}>
            <Wrapper className={styles.metadata}>{`${targetLabel} · ${usageText} ·`}</Wrapper>

            <Wrapper className={`${styles.status} ${statusClass}`}>{statusText}</Wrapper>
        </HorizontalContainer>
    );
};

export default TargetStatusLine;
